import { Decoration, WidgetType } from "@codemirror/view";
import { syntaxTree } from "@codemirror/language";

import { hiddenDecoration, shouldHideMarkers } from "../utils/decorationHelper"

// Helper function to determine media type from URL
function detectMediaType(url) {
  if (!url) return "unknown";

  // Direct video file
  if (url.match(/\.(mp4|webm|ogg)$/i)) return "video";

  // YouTube
  if (url.match(/(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S+\?v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/)) {
    return "youtube";
  }

  // Vimeo
  if (url.match(/vimeo\.com\/([0-9]+)/)) return "vimeo";

  // Default to image for other URLs
  return "image";
}

// Extract YouTube video ID from URL
function getYoutubeVideoId(url) {
  const match = url.match(/(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S+\?v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

// Extract Vimeo video ID from URL
function getVimeoVideoId(url) {
  const match = url.match(/vimeo\.com\/([0-9]+)/);
  return match ? match[1] : null;
}

// FIXME: Alt attribute on image is not being set correctly
function createImageDecoration(url, altText) {
  return Decoration.widget({
    widget: new class extends WidgetType {
      toDOM() {
        let img = document.createElement("img");
        img.src = url;
        img.alt = altText || "";
        img.className = "cm-markdoc-media cm-markdoc-image";
        img.style.maxWidth = "100%"; // Ensure image fits editor width
        img.onerror = () => {
          img.alt = "Failed to load image";
          img.style.border = "1px solid red"; // Visual feedback for broken images
        };
        return img;
      }
    }(),
  });
}

// Create video decoration
function createVideoDecoration(url, altText) {
  return Decoration.widget({
    widget: new class extends WidgetType {
      toDOM() {
        let video = document.createElement("video");
        video.src = url;
        video.controls = true;
        video.title = altText || "Video";
        video.className = "cm-markdoc-media cm-markdoc-video";
        video.style.maxWidth = "100%";
        video.style.maxHeight = "400px";
        video.onerror = () => {
          let fallback = document.createElement("div");
          fallback.textContent = "Failed to load video";
          fallback.className = "cm-markdoc-media-error";
          fallback.style.border = "1px solid red";
          fallback.style.padding = "10px";
          return fallback;
        };
        return video;
      }
    }(),
  });
}

function createYoutubeDecoration(url, altText) {
  const videoId = getYoutubeVideoId(url);
  if (!videoId) return null;

  return Decoration.widget({
    widget: new class extends WidgetType {
      toDOM() {
        let container = document.createElement("div");
        container.className = "cm-markdoc-media cm-markdoc-youtube";

        let iframe = document.createElement("iframe");
        iframe.src = `https://www.youtube.com/embed/${videoId}`;
        iframe.title = altText || "YouTube video";
        iframe.width = "100%";
        iframe.height = "315";
        iframe.allowFullscreen = true;
        iframe.frameBorder = "0";

        container.appendChild(iframe);
        return container;
      }
    }(),
  });
}

// Create Vimeo embed decoration
function createVimeoDecoration(url, altText) {
  const videoId = getVimeoVideoId(url);
  if (!videoId) return null;

  return Decoration.widget({
    widget: new class extends WidgetType {
      toDOM() {
        let container = document.createElement("div");
        container.className = "cm-markdoc-media cm-markdoc-vimeo";

        let iframe = document.createElement("iframe");
        iframe.src = `https://player.vimeo.com/video/${videoId}`;
        iframe.title = altText || "Vimeo video";
        iframe.width = "100%";
        iframe.height = "315";
        iframe.allowFullscreen = true;
        iframe.frameBorder = "0";

        container.appendChild(iframe);
        return container;
      }
    }(),
  });
}

function getImageDetails(view, imageNode) {
  let url = "";
  let altText = "";
  const tree = syntaxTree(view.state);
  if (!tree) {
    console.warn("Syntax tree not available for image node:", imageNode);
    return { url: "", altText: "Media" }; // Updated fallback text
  }

  tree.iterate({
    from: imageNode.from,
    to: imageNode.to,
    enter(child) {
      if (child.name === "URL" || child.name === "ImageDestination" || child.name === "LinkDestination") {
        url = view.state.doc.sliceString(child.from, child.to);
      } else if (child.name === "ImageText" || child.name === "LinkText" || child.name === "LinkLabel") {
        altText = view.state.doc.sliceString(child.from, child.to);
      }
    },
  });
  return { url: url || "", altText: altText || "Media" }; // Updated fallback text
}

export function decorateImage(node, view, widgets, mode) {
  if (shouldHideMarkers(view, node, mode)) {
    const { url, altText } = getImageDetails(view, node);
    if (url) {
      const mediaType = detectMediaType(url);
      let decoration = null;

      switch (mediaType) {
        case "video":
          decoration = createVideoDecoration(url, altText);
          break;
        case "youtube":
          decoration = createYoutubeDecoration(url, altText);
          break;
        case "vimeo":
          decoration = createVimeoDecoration(url, altText);
          break;
        case "image":
        default:
          decoration = createImageDecoration(url, altText);
          break;
      }

      if (decoration) {
        widgets.push(decoration.range(node.from));
        widgets.push(hiddenDecoration(node.from, node.to)); // Hide raw syntax
      }
    }
  }

  return false; // Prevent further processing of Image children
}
